// frontend/src/components/grid/OverrideCellRenderer.jsx
// Shared AG Grid cell renderer for a value a person may override: the system
// value in muted text, a person's value in the override ink
// (`override-cell-user`, OverrideCellRenderer.css). InheritedCellRenderer and
// the promoted-month cell class share the stylesheet.
//
// Required cellRendererParams:
//   getSystemValue(data, colDef) → number|null — the unmodified system-calculated value
//   isModified(data, colDef)     → boolean  — whether a user override is active
// Each grid supplies its OWN pair, so a change to one grid's answer does not
// reach the others.
//
// AG Grid uses the same cellRenderer for regular rows and pinned-bottom totals
// rows, but pinned rows carry only aggregate values — not the per-row fields
// getSystemValue / isModified read — so they render the raw value.

import { useFormatters } from '../../hooks/useFormatters';
import './OverrideCellRenderer.css';

const OverrideCellRenderer = (params) => {
  const { formatNumber } = useFormatters();
  const fmt = (v) => formatNumber(v, 0);

  // Pinned-bottom totals row — aggregated values, no system-vs-override
  // semantics apply. Render the raw value formatted, in neither ink.
  if (params.node?.rowPinned) {
    if (params.value == null) return null;
    return <span>{fmt(params.value)}</span>;
  }

  const { getSystemValue, isModified } = params;
  const overrideValue = params.value;
  const systemValue = getSystemValue(params.data, params.colDef);
  const modified = isModified(params.data, params.colDef);

  if (!modified) {
    if (systemValue == null && overrideValue == null) return <span className="override-cell-auto">—</span>;
    // Not the system's number, yet nothing marks it as a person's: plain text.
    // A value where there is no system number is never the system's.
    if (overrideValue != null
      && (systemValue == null || Math.round(overrideValue) !== Math.round(systemValue))) {
      return <span>{fmt(overrideValue)}</span>;
    }
    return <span className="override-cell-auto">{fmt(systemValue)}</span>;
  }

  // Modified, but the override was cleared (unsaved delete-to-revert).
  // Render the system value in muted style — that's what the cell will
  // show after save, and the ag-cell-unsaved CSS class on the cell
  // already indicates the unsaved-change state.
  if (overrideValue == null) {
    return <span className="override-cell-auto">{fmt(systemValue)}</span>;
  }

  return <span className="override-cell-user">{fmt(overrideValue)}</span>;
};

export default OverrideCellRenderer;
